import { toRaw, unref } from 'vue'
import * as L from 'leaflet'
import 'leaflet-semicircle' // 扇形插件的类型定义
import 'leaflet-ellipse' // 椭圆形插件的类型定义
import LeafletFreeDraw, { freeDraw } from 'leaflet-freedraw'
import { CanvasShipUtils } from '@map-sdk/sdk/utils/CanvasShipUtils'
import type { MyMap } from '@map-sdk/sdk/ShipxyAPISDK'

interface beginOptions {
  shape: string
  pathOptions: any //L.PathOptions | L.CircleOptions
  locate: boolean
}

class DrawUtil {
  public _map: MyMap
  options: {
    shape: string
    templineStyle: { color: string }
    hintlineStyle: { color: string; dashArray: number[] }
    markerStyle: { draggable: boolean }
    pathOptions: Record<string, any>
    draggable: boolean
    snappable: boolean
    finishOnDoubleClick: boolean
  }
  _callback!: {
    sectorEditCallback?: (sector: L.Layer) => void
    sectorMoveCallback?: (sector: L.Layer) => void
    sectorMoveendCallback?: (sector: L.Layer) => void
    sectorMovebeginCallback?: (sector: L.Layer) => void
    sectorMovebeginendCallback?: (sector: L.Layer) => void
    sectorMovestopCallback?: (sector: L.Layer) => void
    sectorMovestopendCallback?: (sector: L.Layer) => void
    ellipseEditCallback?: (ellipse: L.Ellipse) => void
    ellipseMoveCallback?: (ellipse: L.Ellipse) => void
    ellipseMoveendCallback?: (ellipse: L.Ellipse) => void
  }
  _editIcon: L.Icon<L.IconOptions>
  _editSectorInstance: any
  _editEllipseInstance: any
  constructor(map: MyMap) {
    this._map = map
    this.options = {
      shape: 'Polygon',
      templineStyle: {
        color: 'red',
      },
      hintlineStyle: {
        color: 'red',
        dashArray: [5, 5],
      },
      markerStyle: {
        draggable: true,
      },
      pathOptions: {},
      draggable: true,
      snappable: false,
      finishOnDoubleClick: true,
    }
    this._editIcon = L.icon({
      iconUrl: 'https://cdn.xplusship.com/img/images/flag/red_circle.png',
      iconSize: [10, 10],
      iconAnchor: [5, 5],
    })
  }

  begin(options?: Partial<beginOptions>): L.Layer | null {
    let layer: any = null
    if (this._check()) {
      this._map.shipsService?.setZIndex()
      this._setOptions(options)
      switch (this.options.shape) {
        case 'Sector':
        case 'Ellipse':
          layer = new (L.PM as any).Draw.Marker(toRaw(unref(this._map)))
          break
        case 'FreeDraw':
          this._checkType('FreeDraw')
          layer = freeDraw({
            mode: LeafletFreeDraw.CREATE,
            leaveModeAfterCreate: true,
          })
          layer.shapeType = 'FreeDraw'
          layer.getLayer = () => {
            return null
          }
          this._map.addLayer(layer)
          layer.on('markers', (event: any) => {
            if (layer.size() > 0) {
              const latlngs = event.latLngs[0]
              const polygon = L.polygon(
                latlngs,
                this.options.pathOptions
              ).addTo(this._map)
              layer.getLayer = () => {
                return polygon
              }
              this._map._container.style.cursor = 'default'
              this.edit(polygon) // 假设 edit 方法可以接收 polygon
            }
            layer.off()
            layer.clear()
            const freeDrawSVGs = document.querySelectorAll('svg.free-draw')
            // 遍历并移除这些元素
            freeDrawSVGs.forEach((svg) => {
              // 检查父元素是否存在，若存在，则移除该元素
              svg.parentNode?.removeChild(svg)
            })
          })
          break
        default:
          layer = new (L.PM as any).Draw[this.options.shape](this._map)
      }

      if (this.options.shape !== 'FreeDraw') {
        layer.enable(this.options)
      }
      this._map._container.style.cursor = 'crosshair'
    }
    return layer
  }

  // 结束绘制操作
  end(layer: any): void {
    // 假设layer是L.PM.Layer或其子类实例
    if ('disable' in layer) {
      layer.disable() // 禁用编辑功能
    }

    // 检查是否是自由绘制图层
    if (layer.shapeType === 'FreeDraw') {
      const lay = layer.getLayer()
      if (lay) {
        // 如果是自由绘制图层，执行特定操作
        lay.pm.disable() // 禁用图层的PM编辑功能
        layer.mode(0) // 设置自由绘制模式
        // $('svg.free-draw').remove() // 移除自由绘制SVG元素
        const freeDrawSVGs = document.querySelectorAll('svg.free-draw')
        // 遍历并移除这些元素
        freeDrawSVGs.forEach((svg) => {
          // 检查父元素是否存在，若存在，则移除该元素
          svg.parentNode?.removeChild(svg)
        })
      }
    }

    // 恢复所有船只的Z-index
    if (this._map.shipsService) {
      this._map.shipsService.restoreZIndex()
    }

    // 将地图光标设置回默认状态
    this._map._container.style.cursor = 'default'
  }

  // 根据选项显示图层
  show(
    latlngs: L.LatLng[],
    options: { shape: string; [key: string]: any }
  ): L.Layer {
    let layer: L.Layer | undefined

    switch (options.shape) {
      case 'Marker':
        layer = this._showMark(latlngs, options)
        break
      case 'Circle':
        layer = this._showCircle(latlngs, options)
        break
      case 'Line':
        layer = this._showPolyLine(latlngs, options)
        break
      case 'Rectangle':
        layer = this._showRectangle(latlngs as any, options)
        break
      case 'Sector':
        layer = this._showSector(latlngs, options)
        break
      case 'Ellipse':
        layer = this._showEllipse(latlngs, options)
        break
      case 'FreeDraw':
        layer = this._showFreeDraw(latlngs, options)
        break
      case 'Polygon':
        layer = this._showPolygon(latlngs, options)
        break
      default:
        throw new Error(`Unsupported shape type: ${options.shape}`)
    }

    this._map._container.style.cursor = 'default'
    if (
      this._map.shipsService &&
      typeof this._map.shipsService.restoreZIndex === 'function'
    ) {
      this._map.shipsService.restoreZIndex()
    }

    return layer! // 使用!断言存在，实际使用时可能需要null检查
  }

  // 编辑图层
  edit(layer: L.Layer, editOption?: any, zoomOption?: any): void {
    this._map.shipsService && this._map.shipsService.setZIndex()
    const zIndexValue = [null, undefined, true].indexOf(zoomOption)
    if (zIndexValue > -1) {
      if ('getBounds' in layer) {
        this._map.fitBounds((layer as any).getBounds())
      } else {
        this._map.setView((layer as any).getLatLng(), this._map.getZoom() || 12)
      }
    }

    if ('stopAngle' in layer.options) {
      this._editSector?.call(this, layer)
    } else if ('_radiusX' in layer && '_radiusY' in layer) {
      this._editEllipse?.call(this, layer as any)
    } else {
      if ([null, undefined, true].includes(editOption)) {
        ;(layer as any).pm.disable()
      }
      ;(layer as any).pm.enable(this.options)
    }
  }

  // 开始编辑扇区
  _editSector(editSector: L.Layer) {
    this._editSectorInstance = editSector
    const sectorInfo = this._getSectorInfo(this._editSectorInstance)
    const editIcon = this._editIcon
    if (this._editSectorInstance.options.marker_center) {
      this._map.removeLayer(this._editSectorInstance.options.marker_center)
      this._editSectorInstance.options.marker_center = null
    }
    this._editSectorInstance.options.marker_center = L.marker(
      sectorInfo.center_ll,
      {
        draggable: true,
        semiCircle: this._editSectorInstance,
        icon: editIcon,
      } as any
    ).addTo(this._map)
    this._editSectorInstance.options.marker_center
      .on('move', (e: any) => {
        if (
          !e.target.options.semiCircle.options.draggingObj ||
          'center' == e.target.options.semiCircle.options.draggingObj
        ) {
          e.target.options.semiCircle.options.draggingObj = 'center'
          e.target.options.semiCircle.setLatLng(e.latlng)
          const sectorInfo = this._getSectorInfo(e.target.options.semiCircle)
          e.target.options.semiCircle.options.marker_start.setLatLng(
            sectorInfo.startll
          )
          e.target.options.semiCircle.options.marker_end.setLatLng(
            sectorInfo.endll
          )
          e.target.options.semiCircle.options.draggingObj = ''
          this._callback?.sectorEditCallback &&
            this._callback.sectorEditCallback(this._editSectorInstance)
          this._callback?.sectorMoveCallback &&
            this._callback.sectorMoveCallback(this._editSectorInstance)
        }
      })
      .on('moveend', () => {
        this._callback?.sectorMoveendCallback &&
          this._callback.sectorMoveendCallback(this._editSectorInstance)
      })
    if (this._editSectorInstance.options.marker_start) {
      this._map.removeLayer(this._editSectorInstance.options.marker_start)
      this._editSectorInstance.options.marker_start = null
    }
    this._editSectorInstance.options.marker_start = L.marker(
      sectorInfo.startll,
      {
        draggable: true,
        semiCircle: this._editSectorInstance,
        icon: editIcon,
      } as any
    ).addTo(this._map)
    this._editSectorInstance.options.marker_start
      .on('move', (e: any) => {
        if (
          !e.target.options.semiCircle.options.draggingObj ||
          'start' == e.target.options.semiCircle.options.draggingObj
        ) {
          e.target.options.semiCircle.options.draggingObj = 'start'
          const latLng = this._editSectorInstance
            .getLatLng()
            .distanceTo(e.latlng)
          const latLngToBigCircle = CanvasShipUtils.getAngleByLatLngToBigCircle(
            e.target.options.semiCircle.getLatLng(),
            e.latlng
          )
          e.target.options.semiCircle.setStartAngle(latLngToBigCircle)
          e.target.options.semiCircle.setRadius(latLng)
          const sectorInfo = this._getSectorInfo(e.target.options.semiCircle)
          e.target.options.semiCircle.options.marker_end.setLatLng(
            sectorInfo.endll
          )
          e.target.options.semiCircle.options.draggingObj = ''
          this._callback?.sectorEditCallback &&
            this._callback.sectorEditCallback(this._editSectorInstance)
          this._callback?.sectorMovebeginCallback &&
            this._callback.sectorMovebeginCallback(this._editSectorInstance)
        }
      })
      .on('moveend', () => {
        this._callback?.sectorMovebeginendCallback &&
          this._callback.sectorMovebeginendCallback(this._editSectorInstance)
      })
    this._editSectorInstance.options.marker_end &&
      (this._map.removeLayer(this._editSectorInstance.options.marker_end),
      (this._editSectorInstance.options.marker_end = null))
    this._editSectorInstance.options.marker_end = L.marker(sectorInfo.endll, {
      draggable: true,
      semiCircle: this._editSectorInstance,
      icon: editIcon,
    } as any).addTo(this._map)
    this._editSectorInstance.options.marker_end
      .on('move', (e: any) => {
        if (
          !e.target.options.semiCircle.options.draggingObj ||
          'end' === e.target.options.semiCircle.options.draggingObj
        ) {
          e.target.options.semiCircle.options.draggingObj = 'end'
          let sectorInfo = this._getSectorInfo(e.target.options.semiCircle)
          e.target.options.semiCircle.options.marker_start.setLatLng(
            sectorInfo.startll
          )
          const distanceToBigCircle = this._editSectorInstance
            .getLatLng()
            .distanceTo(e.latlng)
          const angleToBigCircle = CanvasShipUtils.getAngleByLatLngToBigCircle(
            e.target.options.semiCircle.getLatLng(),
            e.latlng
          )
          Math.abs(
            angleToBigCircle - e.target.options.semiCircle.startAngle()
          ) > 50
            ? e.target.options.semiCircle.setStopAngle(angleToBigCircle)
            : ((sectorInfo = this._getSectorInfo(e.target.options.semiCircle)),
              e.target.setLatLng(sectorInfo.endll))
          e.target.options.semiCircle.setRadius(distanceToBigCircle)
          e.target.options.semiCircle.options.draggingObj = ''
          this._callback?.sectorEditCallback &&
            this._callback.sectorEditCallback(this._editSectorInstance)
          this._callback?.sectorMovestopCallback &&
            this._callback.sectorMovestopCallback(this._editSectorInstance)
        }
      })
      .on('moveend', () => {
        this._callback?.sectorMovestopendCallback &&
          this._callback.sectorMovestopendCallback(this._editSectorInstance)
      })
  }

  // 开始编辑椭圆
  _editEllipse(ellipse: L.Ellipse): void {
    this._editEllipseInstance = ellipse

    if (this._editEllipseInstance.options.marker_center) {
      this._map.removeLayer(this._editEllipseInstance.options.marker_center)
      this._editEllipseInstance.options.marker_center = null
    }

    const centerLatLng = ellipse.getLatLng()
    const centerIcon = this._editIcon
    const getInfo = this._getEllipseVertices(ellipse)
    this._editEllipseInstance.options.marker_center = L.marker(centerLatLng, {
      draggable: true,
      ellipse: this._editEllipseInstance,
      icon: centerIcon,
    } as any).addTo(this._map)

    console.log(getInfo, 'getInfo')
    getInfo.forEach((item) => {
      L.marker(item, {
        draggable: true,
        ellipse: this._editEllipseInstance,
        icon: centerIcon,
      } as any).addTo(this._map)
    })
    this._editEllipseInstance.options.marker_center
      .on('move', (e: any) => {
        ;((e.target as L.Marker).options as any).ellipse.setLatLng(e.latlng)
        if (this._callback?.ellipseEditCallback) {
          this._callback.ellipseEditCallback(this._editEllipseInstance)
        }
        if (this._callback?.ellipseMoveCallback) {
          this._callback.ellipseMoveCallback(this._editEllipseInstance)
        }
      })
      .on('moveend', () => {
        if (this._callback?.ellipseMoveendCallback) {
          this._callback.ellipseMoveendCallback(this._editEllipseInstance)
        }
      })
  }
  // 获取扇区信息
  _getSectorInfo(sector: {
    getLatLng: () => L.LatLngExpression
    startAngle: () => any
    _radius: any
    stopAngle: () => any
  }): { center_ll: L.LatLngExpression; startll: L.LatLng; endll: L.LatLng } {
    const point = this._map.latLngToLayerPoint(sector.getLatLng()) as any
    const rotatedStart = point.rotated(sector.startAngle(), sector._radius)
    const rotatedEnd = point.rotated(sector.stopAngle(), sector._radius)
    const startll = this._map.layerPointToLatLng(rotatedStart)
    const endll = this._map.layerPointToLatLng(rotatedEnd)
    return {
      center_ll: sector.getLatLng(),
      startll,
      endll,
    }
  }

  // 获取椭圆区域四个定点
  _getEllipseVertices(ellipse: any) {
    // 椭圆的中心点
    const center = ellipse.getLatLng()

    // 椭圆的长轴和短轴长度
    const radiusX = ellipse.options.radiusKm[0] // 长轴长度
    const radiusY = ellipse.options.radiusKm[1] // 短轴长度

    // 椭圆的旋转角度（如果适用）
    const angle = ellipse.options.radiusKm[2]

    // Leaflet 提供的计算目的地点的方法
    const latLngToPoint = (
      latlng: { lat: number; lng: number },
      distance: number,
      angle: number
    ) => {
      const lat = (latlng.lat * Math.PI) / 180,
        lng = (latlng.lng * Math.PI) / 180,
        R = 6378137, // 地球半径，单位为米
        delta = distance / R,
        angleRad = (angle * Math.PI) / 180
      const lat2 = Math.asin(
        Math.sin(lat) * Math.cos(delta) +
          Math.cos(lat) * Math.sin(delta) * Math.cos(angleRad)
      )
      const lng2 =
        lng +
        Math.atan2(
          Math.sin(angleRad) * Math.sin(delta) * Math.cos(lat),
          Math.cos(delta) - Math.sin(lat) * Math.sin(lat2)
        )
      return L.latLng([(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI])
    }

    // 计算长轴顶点
    const majorAxisVertex1 = latLngToPoint(center, radiusX, angle + 90)
    const majorAxisVertex2 = latLngToPoint(center, radiusX, angle - 90)

    // 计算短轴顶点
    // 这里我们假设短轴与长轴垂直，因此角度为长轴角度加上90度
    const minorAxisVertex1 = latLngToPoint(center, radiusY, angle)
    const minorAxisVertex2 = latLngToPoint(center, radiusY, angle + 180)

    // 打印长轴和短轴顶点
    console.log('长轴顶点:', majorAxisVertex1, majorAxisVertex2)
    console.log('短轴顶点:', minorAxisVertex1, minorAxisVertex2)
    return [
      majorAxisVertex1,
      majorAxisVertex2,
      minorAxisVertex1,
      minorAxisVertex2,
    ]
  }

  // 设置回调函数
  setCallback(cb: any): void {
    this._callback = Object.assign(this._callback || {}, cb)
  }

  // 取消编辑
  cancelEdit(layer: any): void {
    // layer 参数类型根据实际情况确定
    if ('_radius' in layer && '_radiusY' in layer) {
      if (this._editSectorInstance) {
        ;['marker_center', 'marker_start', 'marker_end'].forEach(
          (markerKey) => {
            const marker = this._editSectorInstance.options[markerKey]
            if (marker) {
              this._map.removeLayer(marker)
              this._editSectorInstance.options[markerKey] = null
            }
          }
        )
        this._editSectorInstance = null
      }
      if (layer.pm && layer.pm.enable && layer.pm.disable) {
        layer.pm.enable(this.options)
        layer.pm.disable()
      }
    } else if ('_radiusX' in layer && '_radiusY' in layer) {
      if (this._editEllipseInstance) {
        this._map.removeLayer(this._editEllipseInstance.options.marker_center)
        this._editEllipseInstance.options.marker_center = null
        this._editEllipseInstance = null
      }
    } else {
      layer.pm.enable(this.options)
      layer.pm.disable()
    }
    if (this._map.shipsService && this._map.shipsService.restoreZIndex) {
      this._map.shipsService.restoreZIndex()
    }
  }

  // 移除图层
  remove(layerId: any): void {
    // layerId 参数类型根据实际情况确定
    let layerToRemove
    if (layerId.shapeType === 'FreeDraw') {
      layerToRemove = layerId.getLayer && layerId.getLayer()
    } else if (this._map.hasLayer(layerId)) {
      layerToRemove = layerId
    } else {
      layerToRemove = this.getLayer(layerId) // 假设 getLayer 方法已定义并返回 L.Layer 或 null
    }

    if (layerToRemove) {
      this._map.removeLayer(layerToRemove)
    }
  }

  // 根据 layerId 获取图层
  getLayer(layerId: any): L.Layer | null {
    if (!layerId) return null

    // 如果直接是一个图层实例，直接返回
    if (this._map.hasLayer(layerId)) {
      return layerId
    }

    // 如果 layerId 是一个包含 _layer 属性的对象
    if (layerId._layer) {
      layerId._layer._map = this._map
      const layers: L.Layer[] = []

      this._map.eachLayer((layer: L.Layer) => {
        if (
          layer instanceof L.Polyline ||
          layer instanceof L.Marker ||
          layer instanceof L.Circle ||
          layer instanceof L.Rectangle
        ) {
          layers.push(layer)
        }
      })

      layers.filter((layer: L.Layer) => {
        return !!(layer as any).pm
      })

      if (layerId._shape === 'Marker') {
        return layers.find((layer: any) =>
          layerId._layer.getLatLng().equals(layer.getLatLng())
        ) as any
      } else {
        return layers.find((layer: any) =>
          layerId._layer.getBounds().equals(layer.getBounds())
        ) as any
      }
    }

    console.error('参数错误。layer 不存在，或者不是 Layer 或 Shape')
    return null
  }
  // 显示矩形
  _showRectangle(
    latlngs: L.LatLngBounds,
    options?: Partial<beginOptions>
  ): L.Rectangle {
    const layer = this.begin(options) as any
    const rectangle = L.rectangle(latlngs, this.options.pathOptions).addTo(
      this._map
    )
    layer.disable()
    if (options?.locate) {
      this._map.fitBounds(latlngs)
    }
    return rectangle
  }
  // 显示多边形
  _showPolygon(
    latlngs: L.LatLng[],
    options?: Partial<beginOptions>
  ): L.Polygon {
    const layer = this.begin(options) as any
    let lastLatLng: L.LatLng | undefined

    latlngs.forEach((latlng) => {
      if (!layer._hintMarker._snapped) {
        layer._hintMarker.setLatLng(latlng)
      }
      lastLatLng = layer._hintMarker.getLatLng()
      layer._layer.addLatLng(lastLatLng!)
      layer._createMarker(lastLatLng!)
      layer._hintline.setLatLngs([lastLatLng!, lastLatLng!])
    })

    const polygonLatLngs = layer._layer.getLatLngs() as L.LatLng[]
    const polygon = L.polygon(polygonLatLngs, this.options.pathOptions).addTo(
      this._map
    )
    layer.disable()
    if (options?.locate) {
      this._map.fitBounds(layer._layer.getBounds())
    }
    return polygon
  }

  _showPolyLine(
    latlngs: L.LatLng[],
    options?: Partial<beginOptions>
  ): L.Polyline {
    const layer = this.begin(options) as any

    layer._hintMarker._snapped || layer._hintMarker.setLatLng(latlngs[0])

    let lastLatLng: L.LatLng | undefined
    latlngs.forEach((latlng) => {
      if (!layer._hintMarker?._snapped) {
        layer._hintMarker.setLatLng(latlng) // 更新提示标记位置
      }
      lastLatLng = layer._hintMarker.getLatLng()
      layer._layer.addLatLng(lastLatLng!)
      layer._createMarker(lastLatLng!)
      layer._hintline.setLatLngs([lastLatLng!, lastLatLng!])
    })

    const polylineLatLngs = layer._layer.getLatLngs() as L.LatLng[]
    const polyline = L.polyline(
      polylineLatLngs,
      this.options.pathOptions
    ).addTo(this._map)
    layer.disable()

    if (options?.locate) {
      this._map.fitBounds(polyline.getBounds())
    }

    return polyline
  }

  _showMark(latlngs: L.LatLng[], options?: Partial<beginOptions>): L.Marker {
    const layer = this.begin(options) as any
    const firstLatLng = latlngs[0]

    if (!layer._hintMarker._snapped) {
      layer._hintMarker.setLatLng(firstLatLng)
    }

    const markerLatLng = layer._hintMarker.getLatLng()
    const marker = new L.Marker(markerLatLng, this.options.markerStyle).addTo(
      this._map
    )
    layer.disable()

    const zoom = this._map.getZoom() || 12
    if (options?.locate) {
      const lastLatLng = latlngs[latlngs.length - 1]
      this._map.setView(lastLatLng, zoom)
    }

    return marker
  }

  _showCircle(center: L.LatLng[], options: Partial<beginOptions>): L.Circle {
    const layer = this.begin(options) as any
    const hintMarker = layer._hintMarker
    const centerLatLng = center[0]

    if (!hintMarker._snapped) {
      hintMarker.setLatLng(centerLatLng)
    }

    const latLng = hintMarker.getLatLng()
    const circle = L.circle(
      latLng,
      options.pathOptions as L.CircleMarkerOptions
    ).addTo(this._map)

    layer.disable()
    if (options.locate) {
      this._map.fitBounds(circle.getBounds())
    }

    return circle
  }

  _showSector(center: L.LatLng[], options: Partial<beginOptions>) {
    const layer = this.begin(options) as any
    const hintMarker = layer._hintMarker
    const centerLatLng = center[0]

    if (!hintMarker._snapped) {
      hintMarker.setLatLng(centerLatLng)
    }

    this._checkType('sector')

    const radius: number = options.pathOptions?.radius || 10000 // 假设默认半径
    const startAngle = 45 // 假设默认起始角度
    const stopAngle = 135 // 假设默认结束角度

    delete options.pathOptions?.radius

    const sectorOptions: L.PathOptions = {
      radius,
      startAngle,
      stopAngle,
      draggingObj: '',
      fillOpacity: 1,
      ...options.pathOptions,
    }

    const sector = L.semiCircle(centerLatLng, sectorOptions).addTo(this._map)

    layer.disable()
    if (options.locate) {
      this._map.fitBounds(sector.getBounds())
    }

    return sector
  }

  _showEllipse(center: L.LatLng[], options: Partial<beginOptions>): L.Ellipse {
    const layer = this.begin(options) as any
    const hintMarker = layer._hintMarker
    const centerLatLng = center[0]

    if (!hintMarker._snapped) {
      hintMarker.setLatLng(centerLatLng)
    }

    this._checkType('ellipse')

    const radii: [number, number, number] = options.pathOptions?.radius || [
      10000, 6000, 0,
    ] // 假设默认半径
    delete options.pathOptions?.radius

    const ellipseOptions: L.EllipseOptions = {
      fillOpacity: 0.1,
      radiusKm: radii,
      ...options.pathOptions,
    }

    const ellipse = L.ellipse(
      centerLatLng,
      [radii[0], radii[1]],
      radii[2],
      ellipseOptions
    ).addTo(this._map)

    layer.disable()
    if (options.locate) {
      this._map.fitBounds(ellipse.getBounds())
    }

    return ellipse
  }

  _showFreeDraw(
    latlng: L.LatLng[],
    options?: Partial<beginOptions>
  ): L.Polygon {
    this._setOptions(options)
    const polygon = L.polygon(latlng, this.options.pathOptions).addTo(this._map)
    options?.locate && this._map.fitBounds(polygon.getBounds())
    return polygon
  }

  _setOptions(options?: any): void {
    L.Util.setOptions(this, options)
    L.Util.extend((this._map.pm.Draw as any).options, this.options)
  }

  _check() {
    const map = this._map
    if (this._isEmptyObject(map) || null == map)
      throw new Error('The instance of map is null.')
    return true
  }

  _checkType(layer: string): boolean {
    let isHave = true
    switch (layer.toLowerCase()) {
      case 'sector':
        ;(L && L.semiCircle) ||
          (console.error('请先引入绘制扇形插件'), (isHave = false))
        break
      case 'ellipse':
        ;(L && L.ellipse) ||
          (console.error('请先引入绘制椭圆形插件'), (isHave = false))
        break
      case 'freedraw':
        LeafletFreeDraw ||
          (console.error('请先引入自由绘制插件'), (isHave = false))
    }
    return isHave
  }

  _isEmptyObject(object: any): boolean {
    for (const key in object) {
      return false
    }
    return true
  }
}

const drawUtil = (map: MyMap) => {
  if (!map.drawEdit) {
    map.drawEdit = new DrawUtil(map)
  }
  return map.drawEdit
}
export { DrawUtil, drawUtil }
